export type CountryCode = 'IN' | 'US' | 'DEFAULT';

export type RatingBand = {
  code: string;
  label: string;
  meaning: string;
};

export type RatingStandard = {
  id: string;
  name: string;
  countryFocus: string;
  summary: string;
  highlights: string[];
};

export type CompanyType = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  focus: string[];
};

export type AiStage = {
  stage: string;
  title: string;
  body: string;
  aiDoes: string[];
  aiDoesNot: string;
};

export type FundraisingHighlight = {
  title: string;
  body: string;
};

export type SiteContent = {
  countryCode: CountryCode;
  countryLabel: string;
  currency: string;
  topbarTagline: string;
  phone: string;
  phoneHref: string;
  email: string;
  address: string;
  hero: {
    badge: string;
    titleLine1: string;
    titleAccent: string;
    subtitle: string;
    note: string;
    chips: string[];
    formTitle: string;
    formSubtitle: string;
    submitLabel: string;
    comboHighlight: string;
  };
  ratingPolicy: {
    title: string;
    summary: string;
    points: string[];
  };
  ratingStandards: RatingStandard[];
  ratingScale: RatingBand[];
  companyTypes: CompanyType[];
  fundraisingProcedure: {
    title: string;
    summary: string;
    steps: string[];
    highlights: FundraisingHighlight[];
    outcomes: string[];
  };
  howItWorks: Array<{ title: string; body: string }>;
  aiJourney: AiStage[];
  faq: Array<{ q: string; a: string }>;
  about: {
    title: string;
    paragraphs: string[];
  };
};

/** Countries selectable under Universal menu */
export const supportedCountries: Array<{
  code: CountryCode;
  label: string;
  flag: string;
  desc: string;
}> = [
  { code: 'IN', label: 'India', flag: '🇮🇳', desc: 'INR · RBI-aware readiness' },
  { code: 'US', label: 'United States', flag: '🇺🇸', desc: 'USD · US market credit path' },
];

const COUNTRY_STORAGE_KEY = 'micus_country';

export function readStoredCountry(): CountryCode | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (!raw) return null;
    const resolved = resolveContent(raw).countryCode;
    return resolved === 'DEFAULT' ? null : resolved;
  } catch {
    return null;
  }
}

export function writeStoredCountry(code: string) {
  if (typeof window === 'undefined') return;
  try {
    const resolved = resolveContent(code).countryCode;
    if (resolved === 'DEFAULT') {
      localStorage.removeItem(COUNTRY_STORAGE_KEY);
    } else {
      localStorage.setItem(COUNTRY_STORAGE_KEY, resolved);
    }
  } catch {
    /* ignore */
  }
}

/** Switch market and refresh home — never puts country in the URL */
export function switchCountryAndRefresh(code: string) {
  writeStoredCountry(code);
  window.location.assign('/');
}

const sharedRatingScale: RatingBand[] = [
  { code: 'M-AAA', label: 'Highest', meaning: 'Exceptional capacity to meet obligations; strongest facilitation readiness.' },
  { code: 'M-AA', label: 'Very strong', meaning: 'Very strong capacity with modest sensitivity to adverse conditions.' },
  { code: 'M-A', label: 'Strong', meaning: 'Strong capacity; more sensitive to economic shifts than higher bands.' },
  { code: 'M-BBB', label: 'Adequate', meaning: 'Adequate capacity; adequate for many partner conversations with clear caveats.' },
  { code: 'M-BB', label: 'Speculative', meaning: 'Speculative elements; remediation usually improves bankability.' },
  { code: 'M-B', label: 'Highly speculative', meaning: 'Material credit gaps; partner appetite is selective.' },
  { code: 'M-C', label: 'Elevated risk', meaning: 'Elevated default risk without significant remediation.' },
  { code: 'M-D', label: 'Distressed', meaning: 'Distressed or near-default profile for facilitation purposes.' },
];

export const sharedCompanyTypes: CompanyType[] = [
  {
    id: 'startup',
    title: 'Startups & early-stage',
    subtitle: 'Pre-profit to early revenue',
    description:
      'Founders raising growth capital or working capital. Micus emphasises runway clarity, founder track record, unit economics, and document completeness over long credit histories.',
    focus: ['Cap table clarity', 'Burn & runway', 'Traction narrative', 'KYC readiness'],
  },
  {
    id: 'sme',
    title: 'SMEs',
    subtitle: 'Small & mid-sized enterprises',
    description:
      'Established operators needing shorter paths to lenders. We weigh repayment behaviour, banking patterns, GST/tax posture (where relevant), and working-capital cycles.',
    focus: ['Cash-flow quality', 'Banking conduct', 'Receivables health', 'Compliance posture'],
  },
  {
    id: 'midmarket',
    title: 'Mid-market corporates',
    subtitle: 'Scaled operating companies',
    description:
      'Larger balance sheets and multi-facility needs. Assessment deepens into leverage, segment mix, governance, and partner packaging for institutional conversations.',
    focus: ['Leverage & coverage', 'Segment mix', 'Governance', 'Facility mapping'],
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing & industrial',
    subtitle: 'Asset-heavy operators',
    description:
      'Plant, inventory, and supplier dependence matter. We highlight asset cover, order book quality, working-capital turns, and operational risk for partners.',
    focus: ['Asset cover', 'Order book', 'Inventory turns', 'Supplier concentration'],
  },
  {
    id: 'services',
    title: 'Services & SaaS',
    subtitle: 'Recurring or project revenue',
    description:
      'Software and professional services firms. Micus stresses recurring revenue quality, customer concentration, collections discipline, and growth efficiency.',
    focus: ['ARR / backlog', 'Churn & NRR', 'Customer concentration', 'Collections'],
  },
  {
    id: 'trading',
    title: 'Trading & distribution',
    subtitle: 'High-velocity commerce',
    description:
      'Margin thinness and cycle speed dominate. We assess stock turns, counterparty risk, LC/trade docs, and liquidity buffers before partner routing.',
    focus: ['Stock turns', 'Counterparty risk', 'Trade docs', 'Liquidity buffer'],
  },
  {
    id: 'financial',
    title: 'Financial & NBFC-style',
    subtitle: 'Where market-supported',
    description:
      'Entities with financial-services characteristics. Extra care on regulatory posture, portfolio quality signals, and disclosure readiness for specialised partners.',
    focus: ['Regulatory posture', 'Portfolio quality', 'Disclosures', 'Capital structure'],
  },
  {
    id: 'social',
    title: 'Social & mission-driven',
    subtitle: 'Impact with credit discipline',
    description:
      'Non-profits and social enterprises seeking credit-aware fundraising. We translate mission outcomes into repayable or blended structures partners can diligence.',
    focus: ['Mission clarity', 'Revenue mix', 'Grant vs earned', 'Governance'],
  },
];

const sharedAiJourney: AiStage[] = [
  {
    stage: '01',
    title: 'Intake & requirement capture',
    body: 'You share who you are and what you need to raise. AI helps make the first form complete and country-aware so nothing critical is missed early.',
    aiDoes: [
      'Suggests missing fields and country-specific prompts',
      'Flags inconsistent amounts, tenure, or currency',
      'Routes you to the right market pack (India, US, or global)',
    ],
    aiDoesNot: 'AI does not invent your business facts or auto-approve eligibility.',
  },
  {
    stage: '02',
    title: 'Document & data assembly',
    body: 'Checklists differ by country and company type. AI assists extraction and organisation so analysts and partners see a clean pack faster.',
    aiDoes: [
      'Helps classify uploaded documents',
      'Extracts key figures for human verification',
      'Highlights gaps against the country checklist',
    ],
    aiDoesNot: 'Material figures used in assessment must be human-verified where policy requires.',
  },
  {
    stage: '03',
    title: 'Credit assessment',
    body: 'Country-aware methodology produces an internal ranking (M-AAA–M-D) and readiness narrative. AI accelerates analysis; people stay accountable for final judgement.',
    aiDoes: [
      'Surfaces credit drivers, risks, and peer context',
      'Drafts analyst worksheets and remediation notes',
      'Maps signals to the Micus ordinal scale for facilitation',
    ],
    aiDoesNot: 'AI will not independently assign or finalise a regulated rating where human/committee authority is required.',
  },
  {
    stage: '04',
    title: 'Partner & raise matching',
    body: 'Qualified cases connect to lending / capital partners. AI helps match appetite, sector, and risk band so outreach is shorter and more relevant.',
    aiDoes: [
      'Ranks partner fit by country, amount, and risk band',
      'Prepares structured lead summaries for partners',
      'Suggests sequencing when multiple partners are in play',
    ],
    aiDoesNot: 'AI will not share your data with partners without recorded consent and eligibility checks.',
  },
  {
    stage: '05',
    title: 'Fundraising track & alerts',
    body: 'After rating, Micus sequences diligence, offers, and follow-ups. AI keeps status clear so you spend less calendar time chasing updates.',
    aiDoes: [
      'Summarises lead status and next customer actions',
      'Drafts notification copy for material milestones',
      'Flags SLA risks before they become surprises',
    ],
    aiDoesNot: 'AI will not guarantee approval, disbursement, or fundraising success.',
  },
];

const sharedFaqBase = [
  {
    q: 'Is credit rating part of fundraising?',
    a: 'Yes. On Micus, credit rating is the first step of the fundraising journey. Your rating informs investor readiness and shortens the time to raise capital.',
  },
  {
    q: 'What information do I need to start?',
    a: 'Just your name, work email, and phone number on the home page. We then guide you through rating and fundraising steps.',
  },
  {
    q: 'How does AI fit into Micus?',
    a: 'AI assists intake completeness, document extraction, analysis drafts, partner matching, and status summaries. Authorised humans remain accountable for regulated judgement; partners remain accountable for final credit and disbursement.',
  },
];

function baseContent(partial: Omit<SiteContent, 'ratingScale' | 'companyTypes' | 'aiJourney'> & {
  ratingScale?: RatingBand[];
  companyTypes?: CompanyType[];
  aiJourney?: AiStage[];
}): SiteContent {
  return {
    ratingScale: sharedRatingScale,
    companyTypes: sharedCompanyTypes,
    aiJourney: sharedAiJourney,
    ...partial,
  };
}

export const contentByCountry: Record<CountryCode, SiteContent> = {
  IN: baseContent({
    countryCode: 'IN',
    countryLabel: 'India',
    currency: 'INR',
    topbarTagline: 'Rate · Raise · Grow — Built for Indian founders',
    phone: '+91 98863 56363',
    phoneHref: 'tel:+919886356363',
    email: 'one@lab63.org',
    address: 'Bangalore, Karnataka, India',
    hero: {
      badge: 'India · Rating with FundRaising',
      titleLine1: 'Rate first.',
      titleAccent: 'Raise faster.',
      subtitle:
        'Micus helps Indian businesses complete credit rating as part of fundraising — so capital arrives in a shorter span of time.',
      note: 'One journey: Rating with FundRaising. Not two disconnected products.',
      chips: ['RBI-aware readiness', 'INR fundraising path', 'Shorter raise cycles'],
      formTitle: 'Start your rating & raise',
      formSubtitle: 'Rating with FundRaising — name, email, and phone to begin both in one path.',
      submitLabel: 'Begin rating & raise',
      comboHighlight: 'Rating with FundRaising',
    },
    ratingPolicy: {
      title: 'India rating policy',
      summary:
        'We assess business creditworthiness using India-market signals — repayment behaviour, compliance posture, and growth readiness — before opening your fundraising track.',
      points: [
        'Foundational KYC and business identity checks',
        'Credit and repayment pattern review for India operations',
        'Regulatory-aware readiness for debt / equity conversations',
        'Transparent M-AAA–M-D band shared before investor outreach',
      ],
    },
    ratingStandards: [
      {
        id: 'in-micus',
        name: 'Micus India facilitation scale',
        countryFocus: 'India',
        summary:
          'Proprietary ordinal opinion (M-AAA to M-D) used to package readiness for lenders and investors. Facilitation opinion — not a substitute for a SEBI-registered CRA rating unless Micus is separately authorised.',
        highlights: ['M-AAA–M-D bands', 'Analyst / committee gates', 'Partner-ready pack'],
      },
      {
        id: 'in-rbi',
        name: 'RBI-aware credit readiness',
        countryFocus: 'India',
        summary:
          'Intake and diligence language aligned to how Indian lenders think about KYC, repayment conduct, and facility purpose — so your raise conversations start closer to bankable.',
        highlights: ['KYC / AML posture', 'Facility purpose clarity', 'Banking conduct signals'],
      },
      {
        id: 'in-bureau',
        name: 'Bureau & alternative data (lawful use)',
        countryFocus: 'India',
        summary:
          'Where consent and law allow, Micus may incorporate bureau or alternative data alongside financials — always country-configured, never generic global scrapes.',
        highlights: ['Consent-first', 'India data sources', 'Human verification gates'],
      },
      {
        id: 'in-sebi',
        name: 'SEBI CRA pathway (when required)',
        countryFocus: 'India',
        summary:
          'When a formal agency rating is required, Micus prepares rating-ready profiles and routes through appropriate partners or advisors. Earlier CRA references apply only if/when Micus pursues regulated rating-agency activities.',
        highlights: ['Rating-ready dossier', 'Partner CRA handoff', 'Disclosure discipline'],
      },
    ],
    fundraisingProcedure: {
      title: 'India fundraising procedure',
      summary:
        'After rating, Micus sequences your raise so you spend less calendar time between readiness and capital conversations.',
      steps: [
        'Complete home intake (name, email, phone)',
        'Credit rating workspace opens automatically',
        'Rating pack prepared for lenders / investors',
        'Structured raise track with milestones and shorter follow-ups',
      ],
      highlights: [
        {
          title: 'INR-native path',
          body: 'Amounts, tenure, and partner matching framed for Indian market norms and currency.',
        },
        {
          title: 'Active lead visibility',
          body: 'See how many partner leads are live, what they need from you, and what is waiting on the partner.',
        },
        {
          title: 'On-time disbursement focus',
          body: 'SLA clocks and alerts keep operations and you aligned until funds are confirmed.',
        },
      ],
      outcomes: [
        'Shorter diligence loops with a ready rating pack',
        'Clear next actions instead of scattered email trails',
        'Partner offers tracked in one place',
      ],
    },
    howItWorks: [
      { title: '1. Start', body: 'Share name, email, and phone on micusify.com — Rating with FundRaising begins together.' },
      { title: '2. Rate', body: 'AI-assisted, India-aware credit assessment produces your facilitation band and pack.' },
      { title: '3. Raise', body: 'Matched partners and sequenced follow-ups compress time-to-capital.' },
    ],
    faq: [
      ...sharedFaqBase,
      {
        q: 'Does Micus replace a licensed credit rating agency in India?',
        a: 'Micus prepares rating-ready profiles and process guidance. Formal agency ratings, where required by regulation, are handled through appropriate partners or your advisors.',
      },
      {
        q: 'Which currency and market context do you use for India?',
        a: 'India journeys are framed in INR with local compliance and fundraising norms in mind.',
      },
    ],
    about: {
      title: 'Micus for India',
      paragraphs: [
        'Micus exists so founders and operators can get rated and raise capital without wasting months on fragmented paperwork.',
        'In India, we emphasise readiness, clarity, and speed — credit rating sits inside fundraising, not as a disconnected side process.',
      ],
    },
  }),
  US: baseContent({
    countryCode: 'US',
    countryLabel: 'United States',
    currency: 'USD',
    topbarTagline: 'Rate · Raise · Grow — Built for US operators',
    phone: '+1 (415) 555-0163',
    phoneHref: 'tel:+14155550163',
    email: 'hello@micusify.com',
    address: 'San Francisco Bay Area, USA',
    hero: {
      badge: 'United States · Rating with FundRaising',
      titleLine1: 'Score ready.',
      titleAccent: 'Capital sooner.',
      subtitle:
        'Micus helps US businesses complete credit rating as part of fundraising — compressing the time from readiness to capital.',
      note: 'One journey: Rating with FundRaising. Start with a short intake — no GPS permission required.',
      chips: ['US market credit readiness', 'USD raise track', 'Faster investor cycles'],
      formTitle: 'Start your rating & raise',
      formSubtitle: 'Rating with FundRaising — name, email, and phone to launch both together.',
      submitLabel: 'Begin rating & raise',
      comboHighlight: 'Rating with FundRaising',
    },
    ratingPolicy: {
      title: 'US rating policy',
      summary:
        'We evaluate US business credit readiness using market-appropriate signals — payment history, leverage posture, and growth narrative quality — before activating your raise track.',
      points: [
        'Business identity and ownership clarity',
        'US credit and payment behaviour signals',
        'Investor / lender conversation readiness',
        'Clear M-AAA–M-D narrative before outreach',
      ],
    },
    ratingStandards: [
      {
        id: 'us-micus',
        name: 'Micus US facilitation scale',
        countryFocus: 'United States',
        summary:
          'Same proprietary M-AAA–M-D ordinal scale, calibrated for US diligence language so partners see relative risk clearly without mistaking it for a consumer FICO product.',
        highlights: ['M-AAA–M-D bands', 'Business (not consumer) focus', 'Investor-ready narrative'],
      },
      {
        id: 'us-payment',
        name: 'Payment & trade credit signals',
        countryFocus: 'United States',
        summary:
          'Where lawful, Micus weighs payment behaviour, trade references, and banking patterns typical of US small-business and mid-market diligence.',
        highlights: ['Payment conduct', 'Trade references', 'Banking patterns'],
      },
      {
        id: 'us-diligence',
        name: 'Investor / lender diligence pack',
        countryFocus: 'United States',
        summary:
          'Outputs map to how US lenders and investors diligence: leverage, coverage, customer quality, and use of proceeds — packaged for shorter follow-up cycles.',
        highlights: ['Use of proceeds', 'Leverage story', 'Customer quality'],
      },
      {
        id: 'us-compliance',
        name: 'KYC / AML & disclosure posture',
        countryFocus: 'United States',
        summary:
          'Identity, beneficial ownership, and consent flows configured for US expectations before any partner data share.',
        highlights: ['Beneficial ownership', 'Consent records', 'Audit trail'],
      },
    ],
    fundraisingProcedure: {
      title: 'US fundraising procedure',
      summary:
        'Post-rating, Micus structures your raise so diligence loops are shorter and outreach is better sequenced.',
      steps: [
        'Complete home intake (name, email, phone)',
        'Credit rating workspace opens automatically',
        'Investor-ready rating summary generated',
        'Fundraising track with milestones and follow-up cadence',
      ],
      highlights: [
        {
          title: 'USD raise track',
          body: 'Currency, tenure, and partner matching framed for US market conversations.',
        },
        {
          title: 'Diligence compression',
          body: 'A single rating pack reduces repeated document asks across partners.',
        },
        {
          title: 'Offer & milestone tracking',
          body: 'See offers, conditions, and disbursement milestones without spreadsheet chaos.',
        },
      ],
      outcomes: [
        'Clearer readiness story for capital conversations',
        'Fewer stalled diligence threads',
        'Visible path from rating to funds',
      ],
    },
    howItWorks: [
      { title: '1. Start', body: 'Share name, email, and phone — Rating with FundRaising opens as one journey.' },
      { title: '2. Rate', body: 'AI-assisted US credit readiness assessment inside the fundraising flow.' },
      { title: '3. Raise', body: 'Move into capital conversations with a clearer readiness story.' },
    ],
    faq: [
      ...sharedFaqBase,
      {
        q: 'Is this a consumer FICO product?',
        a: 'No. Micus focuses on business credit readiness and fundraising process — not consumer credit scores.',
      },
      {
        q: 'Which currency and market context do you use for the US?',
        a: 'US journeys are framed in USD with US fundraising and diligence norms in mind.',
      },
    ],
    about: {
      title: 'Micus for the United States',
      paragraphs: [
        'Micus helps operators get credit-ready and raise with less wasted calendar time.',
        'In the US, rating is embedded in fundraising so your story, readiness, and outreach stay in one continuous path.',
      ],
    },
  }),
  DEFAULT: baseContent({
    countryCode: 'DEFAULT',
    countryLabel: 'Global',
    currency: 'USD',
    topbarTagline: 'Rate · Raise · Grow — Credit rating inside fundraising',
    phone: '+91 98863 56363',
    phoneHref: 'tel:+919886356363',
    email: 'one@lab63.org',
    address: 'Global · Micusify',
    hero: {
      badge: 'Rating with FundRaising',
      titleLine1: 'Rate first.',
      titleAccent: 'Raise faster.',
      subtitle:
        'Micus combines credit rating with fundraising so customers raise capital in a shorter span of time.',
      note: 'One journey: Rating with FundRaising. Start with name, email, and phone.',
      chips: ['Rating included', 'Faster raises', 'One journey'],
      formTitle: 'Start your rating & raise',
      formSubtitle: 'Rating with FundRaising — begin credit rating as part of your raise.',
      submitLabel: 'Begin rating & raise',
      comboHighlight: 'Rating with FundRaising',
    },
    ratingPolicy: {
      title: 'Rating policy',
      summary: 'We assess credit readiness before activating your fundraising track — with country packs when you select India or the United States.',
      points: [
        'Identity and business basics',
        'Credit readiness signals',
        'Transparent M-AAA–M-D narrative',
        'Fundraising unlock after rating',
      ],
    },
    ratingStandards: [
      {
        id: 'global-micus',
        name: 'Micus universal facilitation scale',
        countryFocus: 'All markets',
        summary:
          'M-AAA to M-D ordinal opinion of relative credit risk for facilitation. Country packs overlay local methodology when you choose India or the United States under Universal.',
        highlights: ['Shared M-AAA–M-D language', 'Country overlays', 'Human final gates'],
      },
      {
        id: 'global-country',
        name: 'Country-aware methodology',
        countryFocus: 'Configured per market',
        summary:
          'Regulation, permitted data, KYC, and partner scorecards differ by jurisdiction. Use Universal → your country to load the right pack.',
        highlights: ['Jurisdiction first', 'Local checklists', 'Partner appetite rules'],
      },
      {
        id: 'in-preview',
        name: 'India standards (supported)',
        countryFocus: 'India',
        summary: 'RBI-aware readiness, lawful bureau use, and SEBI CRA handoff pathway when a formal rating is required.',
        highlights: ['INR path', 'RBI-aware', 'CRA partner route'],
      },
      {
        id: 'us-preview',
        name: 'United States standards (supported)',
        countryFocus: 'United States',
        summary: 'Business (not consumer) credit readiness, payment/trade signals, and USD investor diligence packaging.',
        highlights: ['USD path', 'Non-FICO business focus', 'Diligence pack'],
      },
    ],
    fundraisingProcedure: {
      title: 'Fundraising procedure',
      summary: 'After rating, we sequence your raise for shorter time-to-capital — with country-specific partner networks when available.',
      steps: [
        'Home intake',
        'Credit rating',
        'Raise pack',
        'Capital conversations',
      ],
      highlights: [
        {
          title: 'One continuous journey',
          body: 'Rating unlocks fundraising — you do not restart a separate product.',
        },
        {
          title: 'Partner connection',
          body: 'Qualified cases route to partners with clear status until funds arrive.',
        },
        {
          title: 'Notifications that matter',
          body: 'Alerts for actions, delays, and disbursement confirmation.',
        },
      ],
      outcomes: [
        'Less fragmented paperwork',
        'Faster path from need to funded outcome',
        'Audit trail from requirement → credit → partner → funds',
      ],
    },
    howItWorks: [
      { title: '1. Start', body: 'Share name, email, and phone — Rating with FundRaising begins.' },
      { title: '2. Rate', body: 'AI-assisted credit rating runs as part of fundraising.' },
      { title: '3. Raise', body: 'Move faster with a clear readiness pack and partner track.' },
    ],
    faq: sharedFaqBase,
    about: {
      title: 'About Micus',
      paragraphs: [
        'Micus helps customers get credit-rated and raise funds faster — in one continuous journey.',
      ],
    },
  }),
};

export function resolveContent(code: string | null | undefined): SiteContent {
  const key = (code || 'DEFAULT').toUpperCase();
  if (key === 'IN' || key === 'IND' || key === 'INDIA') return contentByCountry.IN;
  if (key === 'US' || key === 'USA' || key === 'UNITED STATES') return contentByCountry.US;
  return contentByCountry.DEFAULT;
}
